import { useState } from 'react';
import { approveDevice } from '../offline/admin.js';
import { slugify } from '../offline/register.js';
import { createPairingCode, registerShop } from '../offline/register.js';
import { fetchSnapshot, generateKeys, pairDevice } from '../offline/sync.js';
import type { PosStore } from '../offline/store.js';
import { API_BASE } from '../config.js';

interface Props {
  store: PosStore;
  onReady: () => void;
  onCancel: () => void;
}

const MIN_PASSWORD = 10;

/**
 * Daftar kedai baharu dan **terus pasangkan peranti ini**.
 *
 * Pemilik yang membuka POS buat kali pertama belum mempunyai kod pairing — kod itu hanya wujud
 * selepas log masuk. Jadi aliran manual ialah: daftar → log masuk → cipta kod → taip kod → muat
 * turun katalog. Empat langkah sebelum nampak satu produk.
 *
 * Di sini, peranti ini menjadi peranti #1 secara automatik. Peranti kedua (kaunter lain) masih
 * menggunakan kod pairing melalui `/admin`, kerana ia bukan mesin yang mendaftar.
 */
export function RegisterScreen({ store, onReady, onCancel }: Props): React.ReactElement {
  const [shopName, setShopName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agentToken, setAgentToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [error, setError] = useState<string | null>(null);

  const slug = slugify(shopName === '' ? 'kedai-anda' : shopName);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Kata laluan mesti sekurang-kurangnya ${MIN_PASSWORD} aksara`);
      return;
    }

    setBusy(true);
    setError(null);

    try {
      setStep('Mendaftar kedai…');
      const shop = await registerShop(API_BASE, { shopName, ownerName, email, password });

      const outlet = shop.outlets[0];
      if (outlet === undefined) throw new Error('Pelayan tidak mencipta cawangan');

      setStep('Memasangkan peranti ini…');
      const pairing = await createPairingCode(API_BASE, shop.token, {
        outletId: outlet.id,
        name: `Kaunter 1`,
        deviceNo: 1,
      });

      const keys = await generateKeys();
      const paired = await pairDevice(API_BASE, {
        code: pairing.code,
        publicKey: keys.publicKey,
        keyProtection: keys.kind ?? 'exportable',
        agentVersion: 'web-0.1.0',
      });
      const identity = paired.identity;

      // Pemilik hadir semasa mendaftar, jadi peranti ini diluluskan serta-merta. Aliran kod pairing
      // (untuk PC tambahan) memerlukan pengurus menekan Luluskan di back office.
      if (paired.requiresApproval) {
        setStep('Meluluskan peranti…');
        await approveDevice(API_BASE, shop.token, identity.deviceId);
      }
      setStep('Memuat turun katalog…');
      const snapshot = await fetchSnapshot(API_BASE, shop.token);

      await store.saveKeys(keys);
      await store.saveIdentity(identity);
      await store.seedClientSeq(paired.lastClientSeq);
      if (agentToken.trim() !== '') await store.saveAgentToken(agentToken.trim());
      await store.applySnapshot(snapshot);

      onReady();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'pendaftaran gagal';
      setError(
        message.includes('sudah digunakan') || message.includes('SHOP_TAKEN')
          ? 'Nama kedai ini sudah digunakan. Cuba tambah sesuatu, contohnya nama bandar.'
          : message,
      );
      setStep('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="setup">
      <h1>Daftar kedai</h1>
      <p className="muted">
        Peranti ini akan terus menjadi kaunter pertama anda. Selepas ini, anda boleh berjual walaupun
        internet putus.
      </p>

      <form onSubmit={submit}>
        <label>
          Nama kedai
          <input
            value={shopName}
            onChange={(event) => setShopName(event.target.value)}
            placeholder="Kedai Ikan Hiasan 72"
            required
            autoFocus
          />
        </label>
        <p className="muted slug-hint">
          Pautan kedai: <code>{slug}</code> — digunakan semasa log masuk, jadi ingat ia.
        </p>

        <label>
          Nama anda
          <input value={ownerName} onChange={(event) => setOwnerName(event.target.value)} required />
        </label>

        <label>
          E-mel
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        <label>
          Kata laluan (min {MIN_PASSWORD} aksara)
          <input
            type="password"
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={MIN_PASSWORD}
            required
          />
        </label>

        <label>
          Token ejen cetak (pilihan)
          <input
            value={agentToken}
            onChange={(event) => setAgentToken(event.target.value)}
            placeholder="Dipaparkan di konsol ejen pada larian pertama"
            spellCheck={false}
          />
        </label>

        {error !== null && <p className="error">{error}</p>}
        {step !== '' && <p className="muted">{step}</p>}

        <div className="actions">
          <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
            Kembali
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'Memproses…' : 'Daftar dan mula'}
          </button>
        </div>
      </form>
    </div>
  );
}
